// ============================================================
// Dashboard URL contract — the single point of change for where the
// dashboard lives on its host. yom.aapastech.com is shared with the
// marketing website (packages/www-router, docs/www-router.md): the
// dashboard owns the paths below, the website owns everything else.
// Used by the dashboard's router, every emailed/OAuth link and the
// edge router, so they cannot drift apart.
// ============================================================
/** Every signed-in screen lives under this prefix. */
export const DASHBOARD_APP_PREFIX = '/app';
/**
 * Built dashboard assets (Vite `build.assetsDir`). Under the app prefix so the
 * website can never shadow them.
 */
export const DASHBOARD_ASSET_PREFIX = '/app/assets/';
/** Public dashboard pages kept at the host root (emails and OAuth link to them). */
export const DASHBOARD_AUTH_PATHS = {
    login: '/login',
    signup: '/signup',
    /** Old sign-up URL, kept as a redirect to `signup`. */
    register: '/register',
    forgotPassword: '/forgot-password',
    verifyEmail: '/verify-email',
    resetPassword: '/reset-password',
    /** Team invite links: `/accept-invite?token=…` (sign up or sign in, then join the org). */
    acceptInvite: '/accept-invite',
};
/**
 * Claim links for partner-managed accounts: `/claim/<token>` (sign in or up, then own
 * the org; docs/partner-api.md). The API builds the same URL (routes/v2/partner.ts).
 */
export const DASHBOARD_CLAIM_PREFIX = '/claim/';
/** White-label client portals on the dashboard host: `/portal/<slug>/*`. */
export const DASHBOARD_PORTAL_PREFIX = '/portal/';
/**
 * Signed-in screens, relative to the app prefix. Before the website took the
 * host root these were served at `/<segment>`; those URLs redirect to
 * `/app/<segment>` (client side in the dashboard, 301 at the edge).
 */
export const DASHBOARD_APP_SEGMENTS = [
    'overview',
    'realtime',
    'sources',
    'geo',
    'devices',
    'events',
    'conversions',
    'goals',
    'funnels',
    'attribution',
    'retention',
    'quality',
    'sub-tenants',
    'portals',
    'proof',
    'privacy-requests',
    'destinations',
    'exports',
    'usage',
    'projects',
    'dashboards',
    'lenses',
    'agents',
    'docs',
    'settings',
    'billing',
    'admin',
];
/** `/app/<sub>`; `appPath()` is `/app`. Accepts `'overview'`, `'/settings/api-keys'`, `'projects?new=1'`. */
export function appPath(sub = '') {
    const s = sub.replace(/^\/+/, '');
    return s ? `${DASHBOARD_APP_PREFIX}/${s}` : DASHBOARD_APP_PREFIX;
}
/** Signed-in landing page (after login, sign-up, Google sign-in). */
export const DASHBOARD_HOME = appPath('overview');
/** Common emailed destinations, as paths on the dashboard host. */
export const DASHBOARD_LINKS = {
    home: DASHBOARD_HOME,
    billing: appPath('billing'),
    apiKeys: appPath('settings/api-keys'),
    usage: appPath('usage'),
    team: appPath('settings/team'),
    auditLog: appPath('settings/audit-log'),
};
/**
 * Absolute dashboard URL: `dashboardUrl('https://yom.aapastech.com/', '/app/billing')`.
 * An unset origin (misconfigured env) yields the bare path rather than throwing mid-request.
 */
export function dashboardUrl(origin, path) {
    return `${(origin ?? '').replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}
/**
 * Pre-`/app` URL of a signed-in screen → its new path (query/hash are the
 * caller's). `/settings/api-keys` → `/app/settings/api-keys`; null when the
 * path is not a legacy app path.
 */
export function legacyAppRedirect(pathname) {
    const seg = pathname.split('/')[1] ?? '';
    if (!DASHBOARD_APP_SEGMENTS.includes(seg)) {
        return null;
    }
    return `${DASHBOARD_APP_PREFIX}${pathname}`;
}
//# sourceMappingURL=dashboard-paths.js.map